import { getInstallId } from '../../telemetry/identity';
import { trackGateEvent } from '../../telemetry/events';
import { currentWorkerBase, formatCodeInput, normalizeCodeInput, redeemUnlockCode } from '../../telemetry/unlock';
import { getEntitlement } from '../entitlement';
import { PLAY_STORE_URL, getPlayBilling, type PlayBilling } from '../entitlement/play';
import { claimGate, dismissGate, gateStatus, onGameEvent, phaseName, setEntitled } from '../gameApi';
import { writeEntitlement } from '../entitlement/store';
import { registerEscape } from '../keyboard';
import { icon } from '../icons';
import { toast } from '../toast';
import type { ScreenMount } from '../types';
import { panoramaHtml } from './accalmie/tree';

/**
 * Écran d'achat (A112) — phase `gate` du cœur, classe `screen screen--gate`.
 *
 * Le joueur a battu le boss du niveau 2 (ou ouvre l'écran depuis l'Accalmie) :
 * le niveau 3 demande le jeu complet. De haut en bas : bandeau, **panorama de
 * l'arbre complet** (64 nœuds, lecture seule, qui défile seul tant que le
 * joueur n'y touche pas), ce que contient le jeu complet, puis les actions —
 * achat Google Play (dans la TWA) ou lien vers la fiche Play (ailleurs),
 * saisie d'un code, « Plus tard ».
 *
 * Sélecteurs stables (e2e) : `[data-role="gate-tree"]`, `[data-action="buy"]`,
 * `[data-action="store"]`, `[data-action="code-toggle"]`,
 * `[data-role="code-input"]`, `[data-action="code-submit"]`,
 * `[data-role="code-error"]`, `[data-action="later"]`.
 */

/** Vitesse du défilement automatique du panorama (px par seconde). */
export const AUTO_SCROLL_SPEED = 24;
/** Pause du défilement à chaque extrémité (secondes). */
export const AUTO_SCROLL_PAUSE = 1.2;

export interface AutoScrollState {
  /** Position courante (px, fractionnaire : le navigateur arrondit `scrollTop`). */
  pos: number;
  /** 1 : vers le bas ; -1 : vers le haut. */
  dir: 1 | -1;
  /** Pause restante à l'extrémité atteinte (secondes). */
  pause: number;
}

/**
 * Un pas du défilement automatique (**pur**) : descend à `speed` px/s, marque
 * une pause de `pauseSec` en bas, remonte, marque une pause en haut, et ainsi
 * de suite. `max` : course totale (`scrollHeight - clientHeight`) ; sans course,
 * rien ne bouge.
 */
export function stepAutoScroll(
  state: AutoScrollState,
  dtSec: number,
  max: number,
  speed: number = AUTO_SCROLL_SPEED,
  pauseSec: number = AUTO_SCROLL_PAUSE,
): AutoScrollState {
  if (max <= 0 || dtSec <= 0) return { ...state, pos: Math.min(Math.max(state.pos, 0), Math.max(max, 0)) };
  let { pos, dir, pause } = state;
  let left = dtSec;
  // Une trame ne franchit jamais plus d'une extrémité (dt est borné par l'appelant).
  while (left > 0) {
    if (pause > 0) {
      const used = Math.min(pause, left);
      pause -= used;
      left -= used;
      continue;
    }
    const room = dir === 1 ? max - pos : pos;
    const travel = speed * left;
    if (travel < room) {
      pos += dir * travel;
      left = 0;
    } else {
      pos = dir === 1 ? max : 0;
      left -= room / speed;
      dir = dir === 1 ? -1 : 1;
      pause = pauseSec;
    }
  }
  return { pos, dir, pause };
}

/** Message affiché sous le champ pour chaque refus d'un code. */
export { CODE_MESSAGES } from '../unlockCodeForm';
import { CODE_MESSAGES } from '../unlockCodeForm';

const FALLBACK_PRICE = '2,35 €';

export const mountGate: ScreenMount = (container, ctx) => {
  const { game } = ctx;
  const status = gateStatus(game);
  const freeLevels = status?.freeLevels ?? 2;
  const reached = status?.reached === true;
  const from: 'reached' | 'menu' = reached ? 'reached' : 'menu';

  const el = document.createElement('div');
  el.className = 'screen screen--gate';
  el.setAttribute('data-role', 'gate');
  el.innerHTML = `
    <header class="f-banner gate-banner">
      <h1 class="end-title">La suite vous attend</h1>
      <p class="end-lead gate-lead" data-role="gate-lead">${
        reached
          ? `Le niveau ${freeLevels + 1} est à vous dès que le jeu est débloqué.`
          : `Version d’essai : niveaux 1 et ${freeLevels}.`
      }</p>
    </header>
    <div class="gate-tree" data-role="gate-tree" data-scroll="auto" tabindex="0" aria-label="Aperçu de l’arbre de talents complet, défilement automatique"></div>
    <section class="f-panel gate-content" aria-label="Contenu du jeu complet">
      <h2 class="gate-content-title">Le jeu complet</h2>
      <ul class="gate-list" data-role="gate-list">
        <li>${icon('node')}<span>Niveaux 3 à 10 et leurs huit boss, jusqu’au Tricorne</span></li>
        <li>${icon('node')}<span>L’arbre de talents complet : 64 nœuds</span></li>
        <li>${icon('abyss')}<span>Le mode infini : l’Abîme, la Forge, les dés Corrupted</span></li>
        <li>${icon('trophy')}<span>Les classements</span></li>
        <li>${icon('shield')}<span>Aucune publicité, aucun autre achat</span></li>
      </ul>
    </section>
    <div class="gate-actions" data-role="gate-actions">
      <div class="gate-main" data-role="gate-main"></div>
      <button type="button" class="f-btn f-btn--ghost f-btn--block" data-action="code-toggle" aria-expanded="false" aria-controls="gate-code-box">J’ai un code</button>
      <div class="f-panel gate-code" id="gate-code-box" data-role="code-box" hidden>
        <input type="text" class="f-input gate-code-input" data-role="code-input" inputmode="text" autocapitalize="characters" autocomplete="off" autocorrect="off" spellcheck="false" maxlength="14" placeholder="XXXX-XXXX-XXXX" aria-label="Code de déblocage" aria-describedby="gate-code-error">
        <button type="button" class="f-btn f-btn--primary f-btn--block" data-action="code-submit">Valider</button>
        <p class="gate-error" id="gate-code-error" data-role="code-error" role="alert" hidden></p>
      </div>
      <button type="button" class="f-btn f-btn--ghost f-btn--block" data-action="later">${
        reached ? `Plus tard — rejouer le niveau ${freeLevels}` : 'Plus tard'
      }</button>
    </div>
  `;
  container.appendChild(el);

  const treeEl = el.querySelector<HTMLElement>('[data-role="gate-tree"]') as HTMLElement;
  const mainEl = el.querySelector<HTMLElement>('[data-role="gate-main"]') as HTMLElement;
  const codeToggle = el.querySelector<HTMLButtonElement>('[data-action="code-toggle"]') as HTMLButtonElement;
  const codeBox = el.querySelector<HTMLElement>('[data-role="code-box"]') as HTMLElement;
  const codeInput = el.querySelector<HTMLInputElement>('[data-role="code-input"]') as HTMLInputElement;
  const codeSubmit = el.querySelector<HTMLButtonElement>('[data-action="code-submit"]') as HTMLButtonElement;
  const codeError = el.querySelector<HTMLElement>('[data-role="code-error"]') as HTMLElement;
  const laterBtn = el.querySelector<HTMLButtonElement>('[data-action="later"]') as HTMLButtonElement;

  trackGateEvent('gate_shown', { detail: from, level: game.state.level });

  // ───────────────────────────────────────────────── Panorama de l'arbre

  let drawnWidth = 0;
  const draw = (): void => {
    const width = Math.max(300, Math.floor(treeEl.clientWidth || 358));
    drawnWidth = width;
    treeEl.innerHTML = panoramaHtml(game.state.nodes ?? {}, width);
  };
  draw();

  // Défilement automatique (rAF) : descend, remonte, jusqu'au premier geste du joueur — puis plus jamais.
  const reduceMotion =
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let raf = 0;
  let auto = !reduceMotion;
  let scrollState: AutoScrollState = { pos: 0, dir: 1, pause: AUTO_SCROLL_PAUSE };
  let lastT = 0;
  let lastSet = 0;
  const stopAuto = (): void => {
    if (!auto) return;
    auto = false;
    if (raf) window.cancelAnimationFrame(raf);
    raf = 0;
    treeEl.setAttribute('data-scroll', 'stopped');
  };
  const frame = (t: number): void => {
    raf = 0;
    if (!auto || !treeEl.isConnected) return;
    const dt = lastT ? Math.min(0.1, (t - lastT) / 1000) : 0;
    lastT = t;
    const max = treeEl.scrollHeight - treeEl.clientHeight;
    scrollState = stepAutoScroll(scrollState, dt, max);
    lastSet = scrollState.pos;
    treeEl.scrollTop = scrollState.pos;
    raf = window.requestAnimationFrame(frame);
  };
  if (auto) raf = window.requestAnimationFrame(frame);
  else treeEl.setAttribute('data-scroll', 'stopped');
  // Un geste du joueur (toucher, molette, clavier) ou un défilement qui n'est pas le nôtre arrête tout.
  const onUserScroll = (): void => {
    if (auto && Math.abs(treeEl.scrollTop - lastSet) > 2.5) stopAuto();
  };
  for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown'] as const) {
    treeEl.addEventListener(type, stopAuto, { passive: true });
  }
  treeEl.addEventListener('scroll', onUserScroll, { passive: true });

  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(() => {
      if (!treeEl.isConnected) return;
      const width = Math.max(300, Math.floor(treeEl.clientWidth || 358));
      if (Math.abs(width - drawnWidth) >= 1) {
        const top = treeEl.scrollTop;
        draw();
        treeEl.scrollTop = top;
      }
    });
    ro.observe(treeEl);
  }

  // ─────────────────────────────────────────────── Achat / lien vers Play

  let billing: PlayBilling | null = null;
  let busy = false;
  let unlocking = false;

  /** Ouverture réussie (achat ou code) : le cœur passe au niveau dû, sinon retour à l'Accalmie. */
  const finishUnlock = (source: 'play' | 'code', token: string): void => {
    unlocking = true;
    try {
      const api = getEntitlement();
      let claimed: boolean;
      if (api) claimed = api.unlockWith(source, token);
      else {
        // Module non installé (ne devrait pas arriver : `main.ts` l'installe au lancement) : même effet, sans lui.
        writeEntitlement({ source, token, at: new Date().toISOString() });
        setEntitled(game, true);
        claimed = claimGate(game);
      }
      if (!claimed && phaseName(game) === 'gate') dismissGate(game);
    } finally {
      unlocking = false;
    }
  };

  const renderMain = (kind: 'buy' | 'store', price: string): void => {
    if (kind === 'buy') {
      mainEl.innerHTML = `<button type="button" class="f-btn f-btn--buy f-btn--block gate-buy" data-action="buy" aria-busy="false"><span>Débloquer le jeu complet · <span class="num" data-role="buy-price">${price}</span></span></button>`;
      const btn = mainEl.querySelector<HTMLButtonElement>('[data-action="buy"]');
      btn?.addEventListener('click', () => void onBuy(btn));
    } else {
      mainEl.innerHTML = `<a class="f-btn f-btn--buy f-btn--block gate-buy" data-action="store" href="${PLAY_STORE_URL}" target="_blank" rel="noopener">${icon('gold')}<span>Disponible sur Google Play</span></a>
        <p class="gate-hint" data-role="gate-hint">Sur iPhone et sur le web : utilisez un code.</p>`;
    }
  };

  async function onBuy(btn: HTMLButtonElement): Promise<void> {
    if (busy) return;
    const play = billing ?? (await getPlayBilling());
    if (!play) {
      toast('Achat impossible pour l’instant. Réessayez plus tard.');
      return;
    }
    busy = true;
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    trackGateEvent('purchase_started', { detail: 'play', level: game.state.level });
    try {
      const out = await play.purchase();
      if (out.ok) {
        trackGateEvent('purchase_ok', { detail: 'play', level: game.state.level });
        toast('Jeu complet débloqué. Merci !');
        finishUnlock('play', out.token);
      } else if (out.reason === 'cancelled') {
        trackGateEvent('purchase_failed', { detail: 'cancelled', level: game.state.level });
      } else {
        toast('Achat impossible pour l’instant. Réessayez plus tard.');
        trackGateEvent('purchase_failed', { detail: 'error', level: game.state.level });
      }
    } finally {
      busy = false;
      if (btn.isConnected) {
        btn.setAttribute('aria-busy', 'false');
        btn.disabled = false;
      }
    }
  }

  // Dans la TWA, l'API est présente d'emblée : le bouton d'achat s'affiche tout de suite (prix de repli),
  // puis Play confirme (ou non) et donne le vrai prix. Ailleurs : lien vers la fiche et saisie d'un code.
  const maybeTwa = typeof window.getDigitalGoodsService === 'function';
  renderMain(maybeTwa ? 'buy' : 'store', FALLBACK_PRICE);
  let disposed = false;
  if (maybeTwa) {
    void getPlayBilling().then(async (play) => {
      if (disposed) return;
      billing = play;
      if (!play) {
        renderMain('store', FALLBACK_PRICE);
        return;
      }
      const price = await play.price();
      const priceEl = mainEl.querySelector<HTMLElement>('[data-role="buy-price"]');
      if (!disposed && price && priceEl) priceEl.textContent = price;
    });
  }

  // ───────────────────────────────────────────────────────────── Le code

  const showCodeError = (message: string): void => {
    codeError.textContent = message;
    codeError.hidden = message === '';
  };
  const onCodeToggle = (): void => {
    const open = codeBox.hidden;
    codeBox.hidden = !open;
    codeToggle.setAttribute('aria-expanded', String(open));
    if (open) codeInput.focus({ preventScroll: false });
  };
  const onCodeInput = (): void => {
    const formatted = formatCodeInput(codeInput.value);
    if (formatted !== codeInput.value) codeInput.value = formatted;
    if (!codeError.hidden) showCodeError('');
  };
  const onCodeKey = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void onCodeSubmit();
    }
  };
  async function onCodeSubmit(): Promise<void> {
    if (busy) return;
    const normalized = normalizeCodeInput(codeInput.value);
    if (!normalized) {
      showCodeError(CODE_MESSAGES.malformed);
      return;
    }
    busy = true;
    codeSubmit.disabled = true;
    codeSubmit.setAttribute('aria-busy', 'true');
    showCodeError('');
    try {
      const out = await redeemUnlockCode(normalized, { installId: getInstallId(), base: currentWorkerBase() });
      if (out.ok) {
        trackGateEvent('code_ok', { detail: 'code', level: game.state.level });
        toast('Code accepté : jeu complet débloqué.');
        finishUnlock('code', out.token);
      } else {
        trackGateEvent('code_refused', { detail: out.reason, level: game.state.level });
        showCodeError(CODE_MESSAGES[out.reason]);
      }
    } finally {
      busy = false;
      if (codeSubmit.isConnected) {
        codeSubmit.disabled = false;
        codeSubmit.setAttribute('aria-busy', 'false');
      }
    }
  }
  codeToggle.addEventListener('click', onCodeToggle);
  codeInput.addEventListener('input', onCodeInput);
  codeInput.addEventListener('keydown', onCodeKey);
  codeSubmit.addEventListener('click', () => void onCodeSubmit());

  // ─────────────────────────────────────────────────────────── Plus tard

  const later = (): void => {
    trackGateEvent('gate_dismissed', { detail: from, level: game.state.level });
    dismissGate(game);
  };
  laterBtn.addEventListener('click', later);
  const unregisterEscape = registerEscape(el, later);

  // Une ouverture venue d'ailleurs (Google Play restaure l'achat pendant que l'écran est ouvert) :
  // le niveau dû s'ouvre, ou l'écran se referme.
  const offEntitlement = onGameEvent(game, 'entitlement', () => {
    if (unlocking || phaseName(game) !== 'gate') return;
    if (gateStatus(game)?.entitled && !claimGate(game)) dismissGate(game);
  });

  return () => {
    disposed = true;
    stopAuto();
    ro?.disconnect();
    offEntitlement();
    unregisterEscape();
  };
};
